import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { DollarSign, Search, CheckCircle, Clock, CreditCard, Calendar, User, FileText, AlertCircle, X, Edit } from 'lucide-react';

const Caja = () => {
  const navigate = useNavigate();
  const [alumnos, setAlumnos] = useState([]);
  const [terminoBusqueda, setTerminoBusqueda] = useState('');
  
  const [alumnoSeleccionado, setAlumnoSeleccionado] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [configPension, setConfigPension] = useState({ monto: 350, anio: new Date().getFullYear() });

  const [modalPago, setModalPago] = useState(false);
  const [cuotaAPagar, setCuotaAPagar] = useState(null);
  const [datosPago, setDatosPago] = useState({ metodoPago: 'Efectivo', numeroOperacion: '' });

  // NUEVOS ESTADOS PARA EDITAR LA CUOTA
  const [modalEditar, setModalEditar] = useState(false);
  const [datosEdicion, setDatosEdicion] = useState({ id: '', concepto: '', montoTotal: '', fechaVencimiento: '' });

  useEffect(() => { cargarAlumnos(); }, []);

  const cargarAlumnos = async () => {
    const token = localStorage.getItem('token');
    if (!token) return navigate('/login');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Alumnos`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setAlumnos(await res.json());
    } catch (e) { console.error("Error", e); }
  };

  const seleccionarAlumno = async (alumno) => {
    setAlumnoSeleccionado(alumno);
    setTerminoBusqueda(''); 
    cargarEstadoCuenta(alumno.id || alumno.Id);
  };

  const cargarEstadoCuenta = async (alumnoId) => {
    setCargando(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Finanzas/EstadoCuenta/${alumnoId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setCuotas(await res.json());
    } catch (e) { console.error(e); } finally { setCargando(false); }
  };

  const handleGenerarPensiones = async () => {
    if (!alumnoSeleccionado) return;
    if (!window.confirm(`¿Generar Matrícula y 10 Pensiones de S/ ${configPension.monto} para el año ${configPension.anio}?`)) return;
    
    setCargando(true);
    const token = localStorage.getItem('token');
    const alumnoId = alumnoSeleccionado.id || alumnoSeleccionado.Id;
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Finanzas/GenerarPensiones/${alumnoId}?monto=${configPension.monto}&anio=${configPension.anio}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        alert("¡Pensiones generadas exitosamente!");
        cargarEstadoCuenta(alumnoId); 
      } else {
        const errorText = await res.text();
        alert(`Aviso: ${errorText}`);
      }
    } catch (e) { alert("Error de conexión"); } finally { setCargando(false); }
  };

  const abrirModalPago = (cuota) => {
    setCuotaAPagar(cuota);
    setDatosPago({ metodoPago: 'Efectivo', numeroOperacion: '' });
    setModalPago(true);
  };

  const handleProcesarPago = async (e) => {
    e.preventDefault();
    setCargando(true);
    const token = localStorage.getItem('token');
    
    const payload = {
      cuotaId: cuotaAPagar.id || cuotaAPagar.Id,
      montoPagado: cuotaAPagar.montoTotal || cuotaAPagar.MontoTotal,
      metodoPago: datosPago.metodoPago,
      numeroOperacion: datosPago.numeroOperacion
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Finanzas/PagarCuota`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert("¡Pago registrado con éxito!");
        setModalPago(false);
        cargarEstadoCuenta(alumnoSeleccionado.id || alumnoSeleccionado.Id);
      } else {
        alert("Error al procesar el pago.");
      }
    } catch (e) { alert("Error de red."); } finally { setCargando(false); }
  };

  // NUEVO: FUNCIONES PARA EDITAR CUOTA
  const abrirModalEditar = (cuota) => {
    setDatosEdicion({
      id: cuota.id || cuota.Id,
      concepto: cuota.concepto || cuota.Concepto,
      montoTotal: cuota.montoTotal || cuota.MontoTotal,
      fechaVencimiento: (cuota.fechaVencimiento || cuota.FechaVencimiento).split('T')[0] // Cortamos la hora para que el input type="date" lo lea bien
    });
    setModalEditar(true);
  };

  const handleActualizarCuota = async (e) => {
    e.preventDefault();
    setCargando(true);
    const token = localStorage.getItem('token');
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/Finanzas/ActualizarCuota/${datosEdicion.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(datosEdicion)
      });

      if (res.ok) {
        alert("¡Cuota actualizada exitosamente!");
        setModalEditar(false);
        cargarEstadoCuenta(alumnoSeleccionado.id || alumnoSeleccionado.Id);
      } else {
        const err = await res.text();
        alert(`Error: ${err}`);
      }
    } catch (e) { alert("Error de conexión"); } finally { setCargando(false); }
  };

  const alumnosFiltrados = terminoBusqueda ? alumnos.filter(a => {
    const busqueda = terminoBusqueda.toLowerCase();
    const nombres = (a.nombres || a.Nombres || '').toLowerCase();
    const apellidos = (a.apellidos || a.Apellidos || '').toLowerCase();
    const dni = (a.documentoIdentidad || a.DocumentoIdentidad || '').toLowerCase();
    return nombres.includes(busqueda) || apellidos.includes(busqueda) || dni.includes(busqueda);
  }).slice(0, 5) : [];

  const totalDeuda = cuotas.filter(c => (c.estado || c.Estado) === 'Pendiente').reduce((sum, c) => sum + (c.montoTotal || c.MontoTotal), 0);
  const totalPagado = cuotas.filter(c => (c.estado || c.Estado) === 'Pagado').reduce((sum, c) => sum + (c.montoTotal || c.MontoTotal), 0);

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <Sidebar />
      <main className="flex-1 p-6 overflow-y-auto">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <DollarSign className="text-emerald-600" /> Módulo de Caja y Finanzas
          </h1>
          <p className="text-slate-500 mt-1">Busca un estudiante para gestionar sus mensualidades y procesar pagos.</p>
        </header>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          
          <div className="xl:col-span-1 space-y-4">
            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 relative">
              <label className="block text-sm font-bold text-slate-700 mb-2">Buscar Estudiante</label>
              <div className="flex items-center border rounded-lg bg-slate-50 focus-within:ring-2 focus-within:ring-blue-500 overflow-hidden">
                <div className="pl-3 text-slate-400"><Search size={18} /></div>
                <input type="text" value={terminoBusqueda} onChange={e => setTerminoBusqueda(e.target.value)} placeholder="DNI o Apellidos..." className="w-full p-2.5 outline-none bg-transparent text-sm" />
              </div>

              {alumnosFiltrados.length > 0 && (
                <div className="absolute left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg z-10 overflow-hidden mx-5">
                  {alumnosFiltrados.map(al => (
                    <div key={al.id || al.Id} onClick={() => seleccionarAlumno(al)} className="p-3 hover:bg-blue-50 cursor-pointer border-b text-sm flex justify-between items-center">
                      <span className="font-medium text-slate-800">{al.apellidos || al.Apellidos}, {al.nombres || al.Nombres}</span>
                      <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">{al.documentoIdentidad || al.DocumentoIdentidad}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {alumnoSeleccionado ? (
              <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-4 border-b pb-4">
                  <div className="bg-blue-100 p-3 rounded-full text-blue-600"><User size={24} /></div>
                  <div>
                    <h3 className="font-bold text-slate-800 leading-tight">{alumnoSeleccionado.apellidos || alumnoSeleccionado.Apellidos}, {alumnoSeleccionado.nombres || alumnoSeleccionado.Nombres}</h3>
                    <p className="text-xs text-slate-500">DNI: {alumnoSeleccionado.documentoIdentidad || alumnoSeleccionado.DocumentoIdentidad}</p>
                  </div>
                </div>

                {cuotas.length === 0 && !cargando && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-xs font-bold text-amber-800 flex items-center gap-1 mb-3"><AlertCircle size={14}/> Estudiante sin deudas registradas.</p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase">Monto Mensual</label>
                        <input type="number" value={configPension.monto} onChange={e => setConfigPension({...configPension, monto: e.target.value})} className="w-full p-1.5 border rounded text-sm outline-none font-bold text-slate-700" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase">Año Escolar</label>
                        <input type="number" value={configPension.anio} onChange={e => setConfigPension({...configPension, anio: e.target.value})} className="w-full p-1.5 border rounded text-sm outline-none font-bold text-slate-700" />
                      </div>
                    </div>
                    <button onClick={handleGenerarPensiones} disabled={cargando} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white p-2 rounded text-sm font-bold flex justify-center items-center gap-2 transition-colors">
                      <Calendar size={16}/> Generar Cronograma de Pagos
                    </button>
                  </div>
                )}

                {cuotas.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                      <p className="text-[10px] font-bold text-red-600 uppercase mb-1">Deuda Pendiente</p>
                      <p className="text-lg font-black text-red-700">S/ {totalDeuda.toFixed(2)}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                      <p className="text-[10px] font-bold text-green-600 uppercase mb-1">Total Pagado</p>
                      <p className="text-lg font-black text-green-700">S/ {totalPagado.toFixed(2)}</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-400 flex flex-col items-center justify-center h-48">
                <Search size={32} className="mb-2 opacity-50"/>
                <p className="text-sm font-medium">Busca y selecciona un estudiante para ver su información financiera.</p>
              </div>
            )}
          </div>

          <div className="xl:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col min-h-[500px]">
            <div className="bg-slate-50 border-b p-4 flex justify-between items-center">
              <h2 className="font-bold text-slate-800 flex items-center gap-2"><FileText size={18} className="text-blue-600"/> Estado de Cuenta</h2>
            </div>
            
            <div className="flex-1 overflow-auto">
              {cargando ? (
                <div className="flex justify-center items-center h-full text-slate-400 font-medium">Cargando información...</div>
              ) : !alumnoSeleccionado ? (
                <div className="flex justify-center items-center h-full text-slate-400 font-medium">Esperando selección...</div>
              ) : cuotas.length === 0 ? (
                <div className="flex justify-center items-center h-full text-slate-400 font-medium italic">No se encontraron cuotas para este estudiante.</div>
              ) : (
                <table className="w-full text-left border-collapse whitespace-nowrap">
                  <thead className="bg-white sticky top-0 shadow-sm">
                    <tr className="text-xs text-slate-500 uppercase tracking-wide border-b">
                      <th className="p-4 font-bold">Concepto</th>
                      <th className="p-4 font-bold text-center">Vencimiento</th>
                      <th className="p-4 font-bold text-right">Monto</th>
                      <th className="p-4 font-bold text-center">Estado</th>
                      <th className="p-4 font-bold text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {cuotas.map(cuota => {
                      const est = cuota.estado || cuota.Estado;
                      // Ajuste de zona horaria simple
                      const fechaDate = new Date(cuota.fechaVencimiento || cuota.FechaVencimiento);
                      fechaDate.setMinutes(fechaDate.getMinutes() + fechaDate.getTimezoneOffset());
                      const vencimiento = fechaDate.toLocaleDateString('es-PE');
                      
                      const monto = parseFloat(cuota.montoTotal || cuota.MontoTotal).toFixed(2);
                      const esPagado = est === 'Pagado';

                      return (
                        <tr key={cuota.id || cuota.Id} className={`border-b hover:bg-slate-50 transition-colors ${esPagado ? 'bg-slate-50 opacity-70' : ''}`}>
                          <td className={`p-4 font-bold ${esPagado ? 'text-slate-500' : 'text-slate-800'}`}>{cuota.concepto || cuota.Concepto}</td>
                          <td className="p-4 text-center text-slate-500 font-medium">{vencimiento}</td>
                          <td className="p-4 text-right font-black text-slate-700">S/ {monto}</td>
                          <td className="p-4 text-center">
                            {esPagado ? (
                              <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center justify-center gap-1 w-24 mx-auto"><CheckCircle size={12}/> Pagado</span>
                            ) : (
                              <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center justify-center gap-1 w-24 mx-auto"><Clock size={12}/> Pendiente</span>
                            )}
                          </td>
                          <td className="p-4 flex justify-center gap-2">
                            {!esPagado && (
                              <>
                                <button onClick={() => abrirModalPago(cuota)} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-bold text-xs transition-colors shadow-sm">
                                  Cobrar
                                </button>
                                <button onClick={() => abrirModalEditar(cuota)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-2.5 py-1.5 rounded-lg transition-colors border border-slate-200" title="Editar fecha o monto">
                                  <Edit size={14}/>
                                </button>
                              </  >
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>
      </main>

      {/* MODAL PARA COBRAR PAGO */}
      {modalPago && cuotaAPagar && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-blue-600 p-4 text-white flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2"><CreditCard size={18}/> Procesar Pago</h3>
              <button onClick={() => setModalPago(false)} className="text-white/70 hover:text-white transition-colors"><X size={20}/></button>
            </div>
            
            <form onSubmit={handleProcesarPago} className="p-6">
              <div className="bg-slate-50 border rounded-lg p-4 mb-5 text-center">
                <p className="text-xs font-bold text-slate-500 uppercase">{cuotaAPagar.concepto || cuotaAPagar.Concepto}</p>
                <p className="text-3xl font-black text-slate-800 mt-1">S/ {parseFloat(cuotaAPagar.montoTotal || cuotaAPagar.MontoTotal).toFixed(2)}</p>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Método de Pago</label>
                  <select value={datosPago.metodoPago} onChange={e => setDatosPago({...datosPago, metodoPago: e.target.value})} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm outline-none font-medium">
                    <option value="Efectivo">Efectivo</option>
                    <option value="Yape / Plin">Yape / Plin</option>
                    <option value="Transferencia Bancaria">Transferencia Bancaria</option>
                    <option value="Tarjeta de Débito/Crédito">Tarjeta (POS)</option>
                  </select>
                </div>

                {datosPago.metodoPago !== 'Efectivo' && (
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">N° de Operación / Referencia</label>
                    <input type="text" required value={datosPago.numeroOperacion} onChange={e => setDatosPago({...datosPago, numeroOperacion: e.target.value})} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ej: 00456789" />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setModalPago(false)} className="flex-1 p-2.5 rounded-lg font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">Cancelar</button>
                <button type="submit" disabled={cargando} className="flex-1 p-2.5 rounded-lg font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-md flex justify-center items-center gap-2">
                  <CheckCircle size={18}/> Confirmar Pago
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* NUEVO: MODAL PARA EDITAR CUOTA (PRÓRROGAS O BECAS) */}
      {modalEditar && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-sm rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-amber-500 p-4 text-white flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2"><Edit size={18}/> Editar Cuota</h3>
              <button onClick={() => setModalEditar(false)} className="text-white/70 hover:text-white transition-colors"><X size={20}/></button>
            </div>
            
            <form onSubmit={handleActualizarCuota} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Concepto</label>
                <input type="text" required value={datosEdicion.concepto} onChange={e => setDatosEdicion({...datosEdicion, concepto: e.target.value})} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-amber-500" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Monto a Cobrar (S/)</label>
                <input type="number" step="0.01" required value={datosEdicion.montoTotal} onChange={e => setDatosEdicion({...datosEdicion, montoTotal: e.target.value})} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-amber-500 font-bold" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Fecha de Vencimiento</label>
                <input type="date" required value={datosEdicion.fechaVencimiento} onChange={e => setDatosEdicion({...datosEdicion, fechaVencimiento: e.target.value})} className="w-full p-2.5 border rounded-lg bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-amber-500 text-blue-600 font-medium" />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setModalEditar(false)} className="flex-1 p-2.5 rounded-lg font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">Cancelar</button>
                <button type="submit" disabled={cargando} className="flex-1 p-2.5 rounded-lg font-bold text-white bg-amber-500 hover:bg-amber-600 transition-colors shadow-md">
                  Guardar Cambios
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Caja;